// ─────────────────────────────────────────────────────────────────────────
// leadForm.ts — assemble the full CRM payload + the demo/live submit seam.
//
// The complete payload is ALWAYS assembled (inputs + outputs + UTM + page URL +
// size + payback + battery interest + lead score + follow-up script). When
// webhookUrl is empty → demo: show a notice, make NO network call. When set →
// POST the payload. There is no email wall before the first result.
// ─────────────────────────────────────────────────────────────────────────

import type { CalcResult, Scenario } from '../core/types.js';
import type { SolarCalcConfig } from '../config/schema.js';

export interface LeadDetails {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  suburb?: string;
  ownHome?: boolean;
  roofType?: string;
  phase?: string;
  interestedIn?: string[];
  message?: string;
}

export interface CrmPayload {
  source: 'solar-payback-calculator';
  configVersion: string;
  client: string;
  capturedAt: string;
  pageUrl: string;
  utm: Record<string, string>;
  contact: LeadDetails;
  inputs: {
    mode: string;
    postcode: string;
    state: string;
    distributorRegion: string;
    bill: number;
    billPeriod: string;
    usageProfile: string;
    sizeKw: number;
    hasExistingSolar: boolean;
    batteryConsidered: boolean;
  };
  outputs: {
    recommendedSizeKw: number;
    netCost: number;
    annualSaving: number;
    paybackYears: number | null;
    cumulativeNet25: number;
    batteryMarginalPaybackYears: number | null;
    leadScore: number;
  };
  followUpScript: string;
}

function readUtm(search: string): Record<string, string> {
  const utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(search);
    for (const [k, v] of params.entries()) {
      if (k.toLowerCase().startsWith('utm_') || k === 'gclid' || k === 'fbclid') {
        utm[k] = v;
      }
    }
  } catch {
    /* no-op */
  }
  return utm;
}

function followUpScript(result: CalcResult, batteryInterest: boolean): string {
  const pb =
    result.solar.paybackYears != null
      ? `around a ${result.solar.paybackYears}-year payback`
      : 'a long payback on current numbers';
  const battery = batteryInterest
    ? ' They are interested in a battery — note the separate marginal payback when you call.'
    : '';
  return `Lead ran a ${result.recommendedSizeKw} kW estimate showing ${pb} (score ${result.leadScore}). Call to book a site assessment and confirm the roof, switchboard and tariff.${battery}`;
}

/** Assemble the complete CRM payload from the current scenario, result and form. */
export function buildCrmPayload(opts: {
  config: SolarCalcConfig;
  scenario: Scenario;
  result: CalcResult;
  lead: LeadDetails;
  postcode: string;
  state: string;
  distributorRegion: string;
  batteryInterest: boolean;
  mode: string;
  location: { href: string; search: string };
  now: string;
}): CrmPayload {
  const { config, scenario, result, lead, batteryInterest } = opts;
  return {
    source: 'solar-payback-calculator',
    configVersion: config.version,
    client: config.client.clientName,
    capturedAt: opts.now,
    pageUrl: opts.location.href,
    utm: readUtm(opts.location.search),
    contact: lead,
    inputs: {
      mode: opts.mode,
      postcode: opts.postcode,
      state: opts.state,
      distributorRegion: opts.distributorRegion,
      bill: scenario.bill,
      billPeriod: scenario.billPeriod,
      usageProfile: scenario.usageProfile,
      sizeKw: scenario.sizeKw,
      hasExistingSolar: scenario.hasExistingSolar,
      batteryConsidered: batteryInterest,
    },
    outputs: {
      recommendedSizeKw: result.recommendedSizeKw,
      netCost: result.solar.netCost,
      annualSaving: result.solar.year1Savings,
      paybackYears: result.solar.paybackYears,
      cumulativeNet25: result.solar.cumulativeNet25,
      batteryMarginalPaybackYears:
        result.batteryMarginal?.marginalPaybackYears ?? null,
      leadScore: result.leadScore,
    },
    followUpScript: followUpScript(result, batteryInterest),
  };
}

export interface SubmitResult {
  sent: boolean;
  demo: boolean;
  ok?: boolean;
  error?: string;
}

/**
 * Submit a lead. Empty webhookUrl → demo (no network call). Set → POST JSON.
 * Never throws; returns a result the UI can render either way.
 */
export async function submitLead(
  webhookUrl: string,
  payload: CrmPayload,
): Promise<SubmitResult> {
  if (!webhookUrl) {
    // Demo mode: assemble + log, but make NO network call.
    if (typeof console !== 'undefined') {
      console.info('[solar-calculator] demo lead (not sent):', payload);
    }
    return { sent: false, demo: true };
  }
  try {
    const res = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return { sent: true, demo: false, ok: res.ok };
  } catch (err) {
    return { sent: true, demo: false, ok: false, error: String(err) };
  }
}
